import Box from '@/Atoms/Box';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import Spacer from '@/Atoms/Spacer';
import React, {useEffect, useState} from 'react';
import {Text, TouchableOpacity, View, TextInput} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import useUpdateChapterSettings from '@/Hooks/CreateNewChapterApi/useUpdateChapterSettings';
import {useToast} from '@/toast/useToast';
import {styles as workbookStyles} from '../../styles';

type ChapterSettingsPanelProps = {
  chapter: any;
  gameId: string;
  setLoading: (loading: boolean) => void;
  refetchStudy: () => void;
  setSelectedChapter: (chapter: any) => void;
};

const CHAPTER_MODES = [
  {
    key: 'analysis',
    label: 'Normal analysis',
    desc: 'Free board analysis, draw markings, try moves.',
    icon: 'magnify',
  },
  {
    key: 'lesson',
    label: 'Interactive lesson',
    desc: 'Checks moves against the line, shows hint/success popups.',
    icon: 'lightbulb-on-outline',
  },
  // 'practice' (Practice with computer) is hidden for now - still in
  // development. Left out of this list rather than deleted so it's easy to
  // re-enable once ready.
] as const;

const PRACTICE_GOALS = [
  {key: 'none', label: 'No explicit goal'},
  {key: 'mate', label: 'Checkmate the opponent'},
  {key: 'mateIn', label: 'Checkmate in N moves'},
  {key: 'equalize', label: 'Equalize the position'},
  {key: 'advantage', label: 'Reach a winning advantage'},
  {key: 'promotion', label: 'Promote a pawn'},
] as const;

// One selectable row inside a workbookStyles.settingsGroup card - icon,
// title (+ optional description), and a radio dot on the right. Reuses the
// same group/row/icon shapes as the Settings tab's Share & export sub-tab
// (see Study/styles.ts) so both halves of the Settings tab read as one
// design instead of two.
function SelectableRow({
  icon,
  label,
  desc,
  isSelected,
  onPress,
  isLast,
}: {
  icon: string;
  label: string;
  desc?: string;
  isSelected: boolean;
  onPress: () => void;
  isLast?: boolean;
}) {
  return (
    <>
      <TouchableOpacity style={workbookStyles.settingsRow} onPress={onPress}>
        <Box
          style={[
            workbookStyles.settingsRowIcon,
            isSelected && {
              backgroundColor: updateColorWithOpacity(
                CCColors.TextColor.Primary,
                0.14,
              ),
            },
          ]}>
          <MaterialCommunityIcons
            name={icon}
            size={15}
            color={isSelected ? '#A5792F' : '#757575'}
          />
        </Box>
        <Box style={{flex: 1}}>
          <Text
            style={{
              fontSize: 12.5,
              fontWeight: '600',
              color: isSelected ? '#2C1A11' : '#757575',
            }}>
            {label}
          </Text>
          {!!desc && (
            <Text style={{fontSize: 11, color: '#757575', marginTop: 1}}>
              {desc}
            </Text>
          )}
        </Box>
        <MaterialCommunityIcons
          name={isSelected ? 'radiobox-marked' : 'radiobox-blank'}
          size={18}
          color={isSelected ? '#A5792F' : '#B5A99F'}
        />
      </TouchableOpacity>
      {!isLast && <Box style={workbookStyles.settingsRowDivider} />}
    </>
  );
}

// Study's Settings tab, Chapter sub-tab - used to be a modal opened from
// the gear icon on the selected chapter's row in Chapters/index.tsx. That
// gear icon now just switches to this tab (see Study/index.tsx's
// onOpenChapterSettings), and this panel always edits whichever chapter is
// currently selected rather than a separately-tracked "editingChapter".
function ChapterSettingsPanel({
  chapter,
  setLoading,
  refetchStudy,
  setSelectedChapter,
}: ChapterSettingsPanelProps) {
  const [editName, setEditName] = useState('');
  const [editOrientation, setEditOrientation] = useState<'W' | 'B'>('W');
  const [editMode, setEditMode] = useState<'analysis' | 'lesson' | 'practice'>(
    'analysis',
  );
  const [editConcealMoves, setEditConcealMoves] = useState(false);
  // Matches Lichess's practiceGoal chapter tag (mate/mateIn/equalize/
  // advantage/promotion). 'none' means no explicit goal - practice mode
  // then falls back to "reached the end of the authored line".
  const [editPracticeGoalType, setEditPracticeGoalType] = useState<
    'none' | 'mate' | 'mateIn' | 'equalize' | 'advantage' | 'promotion'
  >('none');
  const [editPracticeGoalValue, setEditPracticeGoalValue] = useState('');

  const showToast = useToast();
  const {mutate: updateChapterSettings, isLoading: isUpdatingSettings} =
    useUpdateChapterSettings();

  // Re-seed only when which chapter is selected actually changes (not on
  // every refetch of the same chapter), so an in-progress edit doesn't get
  // clobbered by a background sync while this tab is open.
  useEffect(() => {
    if (!chapter) {
      return;
    }
    setEditName(chapter.name || '');
    setEditOrientation(chapter.orientation || 'W');
    // 'hide_moves' used to be a 4th, mutually-exclusive chapter_mode value.
    // It's now a separate `conceal_moves` toggle that composes with any
    // mode, so an older chapter saved as chapter_mode='hide_moves' reads
    // back as mode='analysis' with concealment already switched on.
    const legacyHideMoves = chapter.chapter_mode === 'hide_moves';
    setEditMode(
      legacyHideMoves ? 'analysis' : chapter.chapter_mode || 'analysis',
    );
    setEditConcealMoves(Boolean(chapter.conceal_moves) || legacyHideMoves);
    const goal = chapter.practice_goal;
    setEditPracticeGoalType(goal?.type || 'none');
    setEditPracticeGoalValue(goal?.value != null ? String(goal.value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id]);

  const handleSaveSettings = () => {
    if (!chapter) {
      return;
    }
    setLoading(true);
    const practiceGoal =
      editMode === 'practice' && editPracticeGoalType !== 'none'
        ? {
            type: editPracticeGoalType,
            ...(editPracticeGoalValue
              ? {value: parseInt(editPracticeGoalValue, 10) || undefined}
              : {}),
          }
        : null;
    updateChapterSettings(
      {
        chapter_id: chapter.id,
        name: editName,
        orientation: editOrientation,
        chapter_mode: editMode,
        conceal_moves: editConcealMoves,
        practice_goal: practiceGoal,
      },
      {
        onSuccess: () => {
          setLoading(false);
          // Apply the saved fields to the in-memory chapter right away, so
          // mode-dependent UI (practice/lesson/conceal) updates immediately
          // instead of waiting on refetchStudy's round trip.
          setSelectedChapter({
            ...chapter,
            name: editName,
            orientation: editOrientation,
            chapter_mode: editMode,
            conceal_moves: editConcealMoves,
            practice_goal: practiceGoal,
          });
          refetchStudy();
        },
        onError: (err: any) => {
          setLoading(false);
          showToast(
            'Failed to update chapter settings' +
              (err?.message ? `: ${err.message}` : ''),
            {type: 'Error'},
          );
        },
      },
    );
  };

  if (!chapter) {
    return (
      <Box style={{padding: 24, alignItems: 'center'}}>
        <MaterialCommunityIcons
          name="chess-rook"
          size={28}
          color={updateColorWithOpacity(CCColors.TextColor.Primary, 0.35)}
        />
        <Spacer spacing={8} />
        <Text
          style={{
            fontSize: 12,
            color: '#757575',
            textAlign: 'center',
          }}>
          Select a chapter to edit its settings.
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{padding: 12, gap: 14}}>
      {/* Chapter Name */}
      <View style={{gap: 6}}>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
          }}>
          CHAPTER NAME
        </Text>
        <TextInput
          value={editName}
          onChangeText={setEditName}
          placeholder="e.g. Chapter 1"
          placeholderTextColor="#A39081"
          style={{
            height: 34,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#EAE2D5',
            backgroundColor: '#FFFFFF',
            paddingHorizontal: 10,
            fontSize: 12,
            color: '#1E110A',
          }}
        />
      </View>

      {/* Board Orientation */}
      <View style={{gap: 6}}>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
          }}>
          BOARD ORIENTATION
        </Text>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: '#F5F5F5',
            borderRadius: 8,
            padding: 3,
            height: 32,
          }}>
          {(['W', 'B'] as const).map(o => (
            <TouchableOpacity
              key={o}
              onPress={() => setEditOrientation(o)}
              activeOpacity={0.8}
              style={{
                flex: 1,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 6,
                backgroundColor:
                  editOrientation === o ? '#FFFFFF' : 'transparent',
                shadowColor: editOrientation === o ? '#000' : 'transparent',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: editOrientation === o ? 0.12 : 0,
                shadowRadius: 2,
                elevation: editOrientation === o ? 1 : 0,
              }}>
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: o === 'W' ? '#FFFFFF' : '#2C1A11',
                  borderWidth: 1,
                  borderColor: '#2C1A11',
                }}
              />
              <Text
                style={{
                  fontSize: 11.5,
                  fontWeight: editOrientation === o ? 'bold' : 'normal',
                  color: editOrientation === o ? '#1E110A' : '#757575',
                }}>
                {o === 'W' ? 'White' : 'Black'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Chapter Mode */}
      <Box>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
            marginBottom: 6,
          }}>
          CHAPTER MODE
        </Text>
        <Box style={workbookStyles.settingsGroup}>
          {CHAPTER_MODES.map((item, idx) => (
            <SelectableRow
              key={item.key}
              icon={item.icon}
              label={item.label}
              desc={item.desc}
              isSelected={editMode === item.key}
              onPress={() => setEditMode(item.key)}
              isLast={idx === CHAPTER_MODES.length - 1}
            />
          ))}
        </Box>
      </Box>

      {/* Practice Goal - hidden along with Practice with computer mode,
          still in development. */}
      {false && editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            PRACTICE GOAL (OPTIONAL)
          </Text>
          <Box style={workbookStyles.settingsGroup}>
            {PRACTICE_GOALS.map((item, idx) => (
              <SelectableRow
                key={item.key}
                icon="target"
                label={item.label}
                isSelected={editPracticeGoalType === item.key}
                onPress={() => setEditPracticeGoalType(item.key)}
                isLast={idx === PRACTICE_GOALS.length - 1}
              />
            ))}
          </Box>
          {editPracticeGoalType === 'mateIn' && (
            <TextInput
              value={editPracticeGoalValue}
              onChangeText={setEditPracticeGoalValue}
              placeholder="Number of moves, e.g. 3"
              placeholderTextColor="#A39081"
              keyboardType="numeric"
              style={{
                height: 34,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: '#EAE2D5',
                backgroundColor: '#FFFFFF',
                paddingHorizontal: 10,
                fontSize: 12,
                color: '#1E110A',
                marginTop: 8,
              }}
            />
          )}
        </Box>
      )}

      {/* Options - "Conceal upcoming moves" (hide moves) is hidden for now,
          still in development. */}

      {/* Save Button */}
      <TouchableOpacity
        style={{
          backgroundColor: '#FACF47',
          paddingVertical: 10,
          borderRadius: 20,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isUpdatingSettings ? 0.5 : 1,
          shadowColor: '#FACF47',
          shadowOffset: {width: 0, height: 2},
          shadowOpacity: 0.15,
          shadowRadius: 4,
          elevation: 2,
          marginTop: 6,
        }}
        onPress={handleSaveSettings}
        disabled={isUpdatingSettings}>
        <Text style={{fontSize: 12.5, color: '#1E110A', fontWeight: 'bold'}}>
          {isUpdatingSettings ? 'Saving…' : 'Save chapter settings'}
        </Text>
      </TouchableOpacity>
    </Box>
  );
}

export default ChapterSettingsPanel;
